/**
 * Party Formation AI - Implements strict DAoC role positioning:
 * - Out-of-combat: tight delta follow (1.0 - 1.5 tiles)
 * - In-combat: front tank, 3.5-5.5 tiles ranged kiting, 1.8-3.0 tiles backline healer/support
 */

import { EntityState, Direction8 } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { ThreatTable } from './ThreatTable';
import { Skills } from './SkillEngine';
import { CrowdControl } from './CCStateMachine';

export interface FormationOffset {
  dx: number;
  dy: number;
}

// 6-Man Bowling Pin Equilateral Triangle & Single File Corridor Offsets
// Row 1: Player Leader at (0, 0)
// Row 2: 2 Companions (Left & Right Flanks)
// Row 3: 3 Companions (Left, Center, Right Rear)
export const TRIANGLE_FORMATION_OFFSETS: FormationOffset[] = [
  // Row 2: 2 companions
  { dx: -0.95, dy: -0.75 },  // Slot 0 (Left Flank)
  { dx: 0.95, dy: -0.75 },   // Slot 1 (Right Flank)
  // Row 3: 3 companions
  { dx: -1.55, dy: -1.45 },  // Slot 2 (Far Left Rear)
  { dx: 0.0, dy: -1.55 },    // Slot 3 (Center Rear)
  { dx: 1.55, dy: -1.45 },   // Slot 4 (Far Right Rear)
];

// Single file corridor queue offsets (Leader leading the line)
export const SINGLE_FILE_OFFSETS: FormationOffset[] = [
  { dx: 0, dy: -0.85 },  // Slot 0
  { dx: 0, dy: -1.65 },  // Slot 1
  { dx: 0, dy: -2.45 },  // Slot 2
  { dx: 0, dy: -3.25 },  // Slot 3
  { dx: 0, dy: -4.05 },  // Slot 4
];

export class PartyFormationAI {
  private corridorMode = false;

  public setCorridorMode(enabled: boolean): void {
    this.corridorMode = enabled;
  }

  public isCorridorMode(): boolean {
    return this.corridorMode;
  }

  public toggleCorridorMode(): boolean {
    this.corridorMode = !this.corridorMode;
    return this.corridorMode;
  }
  /**
   * Determine if the party is actively engaged in combat
   */
  public getPartyCombatTarget(
    player: EntityState,
    monsters: EntityState[]
  ): EntityState | null {
    // 1. If player actively targeted a monster
    if (player.targetId) {
      const target = monsters.find((m) => m.id === player.targetId && !m.isDead);
      if (target) return target;
    }

    // 2. Check if any monster has aggro on party members
    for (const m of monsters) {
      if (m.isDead || !m.threatTable) continue;
      for (const entityId in m.threatTable) {
        if (m.threatTable[entityId] > 0) {
          return m;
        }
      }
    }

    return null;
  }

  /**
   * Main AI tick for all companions in the party
   */
  public updateCompanions(
    dt: number,
    player: EntityState,
    registry: EntityRegistry
  ): void {
    const companions = registry.getByCategory('companion');
    const monsters = registry.getMonsters();
    const activeCombatTarget = this.getPartyCombatTarget(player, monsters);

    // Stable slot sorting order (Paladin -> Ranger -> Mage -> Monk -> Bard -> Cleric)
    const rolePriority: Record<string, number> = {
      tank: 1,
      ranged_dps: 2,
      caster_dps: 3,
      monk_hybrid: 4,
      buffer: 5,
      healer: 6,
    };

    companions.sort((a, b) => (rolePriority[a.role] || 99) - (rolePriority[b.role] || 99));

    for (let slotIndex = 0; slotIndex < companions.length; slotIndex++) {
      const comp = companions[slotIndex];
      if (comp.isDead) continue;

      if (!activeCombatTarget) {
        // --- OUT OF COMBAT: BOWLING PIN TRIANGLE / SINGLE FILE ---
        comp.inCombat = false;
        this.updateOutOfCombatFollow(comp, player, slotIndex, dt, registry);
      } else {
        // --- IN COMBAT: DAoC ROLE BASED POSITIONING ---
        comp.inCombat = true;
        this.updateInCombatBehavior(comp, player, activeCombatTarget, monsters, dt, registry);
      }
    }
  }

  /**
   * Out of combat tight following logic
   * Dynamically positions around the active player at apex
   */
  private updateOutOfCombatFollow(
    comp: EntityState,
    leader: EntityState,
    slotIndex: number,
    dt: number,
    registry: EntityRegistry
  ): void {
    if (!CrowdControl.canMove(comp)) return;

    // Select offset scheme: Single file vs Bowling pin equilateral triangle
    const offsets = this.corridorMode ? SINGLE_FILE_OFFSETS : TRIANGLE_FORMATION_OFFSETS;
    const baseOffset = offsets[Math.min(slotIndex, offsets.length - 1)] || { dx: 0, dy: -1.2 };

    // Rotate offset to match leader's facing direction
    const rotated = this.rotateOffsetByDirection(baseOffset, leader.dir);

    // Target position in world
    const targetX = leader.x + rotated.dx;
    const targetY = leader.y + rotated.dy;

    const dx = targetX - comp.x;
    const dy = targetY - comp.y;
    const distToFormation = Math.sqrt(dx * dx + dy * dy);

    // If companion is close enough, stop smoothly
    if (distToFormation < 0.25) {
      comp.vx = 0;
      comp.vy = 0;
      return;
    }

    // Dynamic catch-up speed: if lagging behind (> 2.0 tiles), accelerate!
    let speed = leader.speed;
    if (distToFormation > 2.8) {
      speed = leader.speed * 1.6;
    } else if (distToFormation > 1.4) {
      speed = leader.speed * 1.25;
    }

    const moveStep = Math.min(distToFormation, speed * dt);
    comp.x += (dx / distToFormation) * moveStep;
    comp.y += (dy / distToFormation) * moveStep;
    comp.dir = this.calcDirection(dx, dy);

    // Cancel casting if moved
    if (comp.cast) {
      Skills.interruptCast(comp, 'movement');
    }
  }

  /**
   * Rotate formation offset relative to leader's 8-way facing direction
   */
  private rotateOffsetByDirection(offset: FormationOffset, dir: Direction8): FormationOffset {
    // Default formation is designed for looking South ('S' or 'SE' in iso)
    // S: behind is North (dy < 0)
    // N: behind is South (dy > 0)
    // E: behind is West (dx < 0)
    // W: behind is East (dx > 0)
    switch (dir) {
      case 'N':
        return { dx: -offset.dx, dy: -offset.dy };
      case 'E':
        return { dx: offset.dy, dy: -offset.dx };
      case 'W':
        return { dx: -offset.dy, dy: offset.dx };
      case 'NE':
        return { dx: -offset.dy * 0.7 - offset.dx * 0.7, dy: -offset.dy * 0.7 + offset.dx * 0.7 };
      case 'NW':
        return { dx: -offset.dx * 0.7 + offset.dy * 0.7, dy: -offset.dy * 0.7 - offset.dx * 0.7 };
      case 'SE':
        return { dx: offset.dx * 0.7 + offset.dy * 0.7, dy: offset.dy * 0.7 - offset.dx * 0.7 };
      case 'SW':
        return { dx: offset.dx * 0.7 - offset.dy * 0.7, dy: offset.dy * 0.7 + offset.dx * 0.7 };
      case 'S':
      default:
        return offset;
    }
  }

  /**
   * In-combat role execution
   */
  private updateInCombatBehavior(
    comp: EntityState,
    player: EntityState,
    mainEnemy: EntityState,
    monsters: EntityState[],
    dt: number,
    registry: EntityRegistry
  ): void {
    if (!CrowdControl.canAct(comp)) return;

    // 1. HEALER ROLE (Lyra - Cleric, Tuck - Friar)
    if (comp.role === 'healer' || comp.role === 'monk_hybrid') {
      this.handleHealerBehavior(comp, player, mainEnemy, dt, registry);
      return;
    }

    // 2. BUFFER / BARD ROLE (Robin - Bard)
    if (comp.role === 'buffer') {
      this.handleBardBehavior(comp, player, mainEnemy, monsters, dt, registry);
      return;
    }

    // 3. RANGED DPS (Caelen - Ranger, Eldryn - Wizard)
    if (comp.role === 'ranged_dps' || comp.role === 'caster_dps') {
      this.handleRangedDpsBehavior(comp, mainEnemy, dt, registry);
      return;
    }
  }

  private handleHealerBehavior(
    comp: EntityState,
    player: EntityState,
    mainEnemy: EntityState,
    dt: number,
    registry: EntityRegistry
  ): void {
    // Maintain safe distance behind the player/tank (2.0 ~ 3.0 tiles)
    const distToPlayer = registry.getDistance(comp, player);
    const distToEnemy = registry.getDistance(comp, mainEnemy);

    // Check if lowest ally needs heal
    const injuredAlly = registry.getNearestInjuredAlly(comp, 8.0);
    if (injuredAlly && (!comp.cooldowns[7] || comp.cooldowns[7] <= 0) && !comp.cast) {
      // Start Emergency Mend (Slot 7)
      comp.targetId = injuredAlly.id;
      Skills.useSkill(comp, 7, registry);
      return;
    }

    // If currently casting heal, don't move!
    if (comp.cast) return;

    // Reposition: stay 2.2 tiles behind the player, away from enemy
    if (distToEnemy < 2.0 && CrowdControl.canMove(comp)) {
      // Kite away from enemy
      const edx = comp.x - mainEnemy.x;
      const edy = comp.y - mainEnemy.y;
      const elen = Math.sqrt(edx * edx + edy * edy) || 1;
      comp.x += (edx / elen) * comp.speed * 0.9 * dt;
      comp.y += (edy / elen) * comp.speed * 0.9 * dt;
    } else if (distToPlayer > 3.2 && CrowdControl.canMove(comp)) {
      // Move closer to player
      const pdx = player.x - comp.x;
      const pdy = player.y - comp.y;
      const plen = Math.sqrt(pdx * pdx + pdy * pdy) || 1;
      comp.x += (pdx / plen) * comp.speed * dt;
      comp.y += (pdy / plen) * comp.speed * dt;
    }
  }

  private handleBardBehavior(
    comp: EntityState,
    player: EntityState,
    mainEnemy: EntityState,
    monsters: EntityState[],
    dt: number,
    registry: EntityRegistry
  ): void {
    // Keep Hymn of Vigor (Slot 6) active
    const hasVigor = comp.buffs.some((b) => b.id === 'hymn_of_vigor');
    if (!hasVigor && (!comp.cooldowns[6] || comp.cooldowns[6] <= 0)) {
      Skills.useSkill(comp, 6, registry);
    }

    // Mez secondary add if present
    const addEnemy = monsters.find(
      (m) => m.id !== mainEnemy.id && !m.isDead && !m.cc && registry.getDistance(comp, m) <= 6.0
    );
    if (addEnemy && (!comp.cooldowns[2] || comp.cooldowns[2] <= 0)) {
      CrowdControl.applyCC(addEnemy, 'mez', 10.0, comp.name);
      comp.cooldowns[2] = 8.0;
    }

    // Maintain 2.0 - 3.0 tiles support position
    const distToPlayer = registry.getDistance(comp, player);
    if (distToPlayer > 3.0 && CrowdControl.canMove(comp)) {
      const pdx = player.x - comp.x;
      const pdy = player.y - comp.y;
      const plen = Math.sqrt(pdx * pdx + pdy * pdy) || 1;
      comp.x += (pdx / plen) * comp.speed * dt;
      comp.y += (pdy / plen) * comp.speed * dt;
    }
  }

  private handleRangedDpsBehavior(
    comp: EntityState,
    enemy: EntityState,
    dt: number,
    registry: EntityRegistry
  ): void {
    const dist = registry.getDistance(comp, enemy);
    const optimalMin = 3.5;
    const optimalMax = 5.2;

    // Kite backward if enemy closes in
    if (dist < optimalMin && CrowdControl.canMove(comp)) {
      const dx = comp.x - enemy.x;
      const dy = comp.y - enemy.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      comp.x += (dx / len) * comp.speed * dt;
      comp.y += (dy / len) * comp.speed * dt;
    } else if (dist > optimalMax && CrowdControl.canMove(comp)) {
      // Approach to optimal range
      const dx = enemy.x - comp.x;
      const dy = enemy.y - comp.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      comp.x += (dx / len) * comp.speed * dt;
      comp.y += (dy / len) * comp.speed * dt;
    }

    // CombatModule.ts handles unified auto-attacks, animations, and projectiles
  }

  private calcDirection(dx: number, dy: number): Direction8 {
    const angle = Math.atan2(dy, dx);
    const deg = (angle * 180) / Math.PI;
    if (deg >= -22.5 && deg < 22.5) return 'E';
    if (deg >= 22.5 && deg < 67.5) return 'SE';
    if (deg >= 67.5 && deg < 112.5) return 'S';
    if (deg >= 112.5 && deg < 157.5) return 'SW';
    if (deg >= 157.5 || deg < -157.5) return 'W';
    if (deg >= -157.5 && deg < -112.5) return 'NW';
    if (deg >= -112.5 && deg < -67.5) return 'N';
    return 'NE';
  }
}

export const FormationAI = new PartyFormationAI();
